import React, { useState, useEffect } from "react";
import { z } from "zod";
import { api, rememberAdminTenantSlug, extractApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Sparkles, Palette, Type, Layers, Rocket, ChevronRight, ChevronLeft, ExternalLink, Loader2, Globe, RefreshCw, Check, ToggleRight } from "lucide-react";
import { TEMPLATES } from "@/templates/registry";
import { PLATFORM_DOMAIN, publicSiteUrl, tenantSiteUrl } from "@/lib/tenantHost";
import { useT, useLanguage } from "@/lib/LanguageContext";
// The content step edits three of the canonical site-copy slots — the SAME content_blocks
// keys Admin -> Site Content edits and the public site resolves. The prefill/upsert rules
// (strictly one language, never overwrite the other) live with them in lib/siteContent.js.
import { buildContentPrefill, buildContentUpserts } from "@/lib/siteContent";
// Relative, not "@/...": the validation infrastructure is imported for real under Jest
// (no "@" alias there) — same reasoning as lib/api.js requiring "./apiErrors".
import useFieldErrors from "../hooks/useFieldErrors";
import FieldError, { invalidInputClass } from "../components/ui/FieldError";
import { DOMAIN_RE } from "../lib/validation";
import { apiErrorCode } from "../lib/apiErrors";
import { adminHref } from "./adminPath";

// DOMAIN_RE is the ONE shared format rule (lib/validation.js — same constant
// CustomDomainAdmin.jsx's dedicated page imports, not a lookalike copy). Optional here: a
// blank custom domain is a legitimate "no custom domain" choice in THIS wizard (unlike
// CustomDomainAdmin's own page, which treats the field as required); only a NON-blank
// value must look like a domain.
const ADDRESS_SCHEMA = z.object({ customDomain: z.string() }).superRefine((val, ctx) => {
  const d = val.customDomain.trim().toLowerCase();
  if (d && !DOMAIN_RE.test(d)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["customDomain"], message: "customDomain.invalidFormat" });
  }
});

const SECTIONS = [
  { key: "story", labelKey: "siteBuilder.sections.story" },
  { key: "approach", labelKey: "siteBuilder.sections.approach" },
  { key: "services", labelKey: "siteBuilder.sections.services" },
  { key: "testimonials", labelKey: "siteBuilder.sections.testimonials" },
  { key: "blog", labelKey: "siteBuilder.sections.blog" },
  { key: "contact", labelKey: "siteBuilder.sections.contact" },
];

const STEPS = [
  { id: "template", labelKey: "siteBuilder.steps.template", icon: Layers },
  { id: "branding", labelKey: "siteBuilder.steps.branding", icon: Palette },
  { id: "content", labelKey: "siteBuilder.steps.content", icon: Type },
  { id: "sections", labelKey: "siteBuilder.steps.sections", icon: ToggleRight },
  { id: "address", labelKey: "siteBuilder.steps.address", icon: Globe },
  { id: "publish", labelKey: "siteBuilder.steps.publish", icon: Rocket },
];

/**
 * SiteBuilderWizard — choix d'un des 6 modèles de site distincts, branding,
 * contenu, sections affichées (toggles), adresse (slug + domaine), aperçu live + publication.
 */
export default function SiteBuilderWizard() {
  const t = useT("admin");
  const tc = useT("common");
  const tt = useT("templates");
  const { lang } = useLanguage();
  const [step, setStep] = useState(0);
  const [siteTemplate, setSiteTemplate] = useState("t1_classic");
  const [toggles, setToggles] = useState({});
  const [branding, setBranding] = useState({ primary_color: "#0B1020", accent_color: "#E6B85C", secondary_color: "#7FB8A4", tagline: "", logo_url: "" });
  const [content, setContent] = useState({ heroTitle: "", heroSub: "", servicesLead: "" });
  // Saved content_blocks rows, keyed by content key — the wizard must PRESERVE the
  // language it is not editing instead of overwriting it with the current one.
  const [contentBlocks, setContentBlocks] = useState({});
  const [subdomain, setSubdomain] = useState("");
  const [customDomain, setCustomDomain] = useState("");
  const [tenant, setTenant] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const navigate = useNavigate();
  const { fieldErrors, validate, clearFieldError } = useFieldErrors();

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/tenant/self/info");
      setTenant(data);
      setSiteTemplate(data.site_template || "t1_classic");
      setToggles(data.section_toggles || {});
      setSubdomain(data.subdomain || data.slug || "");
      if (data.branding) setBranding((b) => ({ ...b, ...data.branding }));
      if (data.custom_domain) setCustomDomain(data.custom_domain);

      // Seed the copy fields from what is ALREADY saved, not from the tenant record: the
      // wizard writes the same keys as Site Content, and reaching the Publish step
      // auto-saves. Seeding from the tenant record would silently overwrite copy the
      // practitioner wrote in Site Content with their own business name.
      const byKey = {};
      try {
        const { data: blocks } = await api.get("/admin/content");
        for (const b of blocks || []) byKey[b.key] = b;
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.debug("[SiteBuilder] content blocks:", e?.message);
      }
      setContentBlocks(byKey);
      setContent(buildContentPrefill(byKey, data, lang));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The content step edits ONE language's column, so switching the admin language must
  // reload that language's saved text. Carrying the previous language's text into the new
  // language would write it to the other column on the next save — the same clobber the
  // strict prefill exists to prevent.
  useEffect(() => {
    if (!tenant) return;
    setContent(buildContentPrefill(contentBlocks, tenant, lang));
  }, [lang, tenant, contentBlocks]);

  const selectTemplate = (tpl) => {
    setSiteTemplate(tpl.id);
    // Le modèle Classic conserve les couleurs existantes ; les autres appliquent leur palette par défaut.
    if (tpl.id !== "t1_classic") {
      setBranding((b) => ({ ...b, primary_color: tpl.colors.primary, accent_color: tpl.colors.accent, secondary_color: tpl.colors.secondary }));
    }
  };

  const toggleSection = (key) => setToggles((t) => ({ ...t, [key]: t[key] === false ? true : false }));
  const isOn = (key) => toggles[key] !== false;

  // Validated on click, the same recipe as a save — never by disabling the button on
  // content validity (house rule). Only the address step has anything to check today
  // (a malformed custom domain); every other step has nothing required and always advances.
  const next = () => {
    if (STEPS[step]?.id === "address" && validate(ADDRESS_SCHEMA, { customDomain })) {
      toast.error(tc("validation.fixHighlighted"));
      return;
    }
    goToStep(Math.min(step + 1, STEPS.length - 1));
  };
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  // A refused save, worded the same way wherever it happens (the auto-save on arriving at the
  // Publish step, Refresh preview, Publish): a known code (e.g. ADDRESS_RETIRED, the address of
  // a deleted practice) reads in the UI language through the common `tc`.
  const saveFailed = (e) => toast.error(extractApiError(e, t("siteBuilder.toast.error"), tc));

  /** Go to a step; arriving at Publish auto-saves and reloads the preview. */
  const goToStep = (i) => {
    setStep(i);
    if (STEPS[i]?.id === "publish" && tenant) {
      // The practitioner is told at once when the save is refused: a silent failure left the
      // preview and the public address on their old values with no message until a later click.
      saveAll().then(() => setPreviewKey((k) => k + 1)).catch(saveFailed);
    }
  };

  // previewSlug drives the in-app, same-origin preview (/t/{slug}) — the backend resolves that
  // internal path prefix by the tenant's `slug` field (tenant_middleware.py), which is distinct
  // from the public `subdomain` field edited below. Keep it slug-based so the preview keeps working.
  const previewSlug = tenant?.slug || subdomain || "";
  // publicSubdomain/liveSiteUrl are the REAL public address (<subdomain>.PLATFORM_DOMAIN),
  // adapted to the current environment by publicSiteUrl (dev: http://<sub>.localhost:3000).
  // A LIVE custom domain outranks the subdomain (tenantSiteUrl, same precedence as
  // the sidebar's View site link) — but only when the subdomain field is untouched:
  // a just-typed subdomain must preview live before it is saved.
  const publicSubdomain = tenant?.subdomain || subdomain || "";
  const liveSiteUrl =
    tenant?.custom_domain_status === "live" && (!subdomain || subdomain === tenant?.subdomain)
      ? tenantSiteUrl(tenant)
      : publicSiteUrl(publicSubdomain);

  /** Persiste tout (modèle, sections, branding, contenu, sous-domaine, domaine). Renvoie le sous-domaine effectif. */
  const saveAll = async () => {
    await api.put("/tenant/site-template", { site_template: siteTemplate, section_toggles: toggles });
    await api.put("/tenant/branding", branding);
    let effectiveSubdomain = tenant?.subdomain || subdomain;
    if (subdomain && subdomain !== tenant?.subdomain) {
      const { data } = await api.put("/tenant/subdomain", { subdomain });
      effectiveSubdomain = data.subdomain;
      setSubdomain(data.subdomain);
      setTenant((t) => ({ ...t, subdomain: data.subdomain, slug: data.slug }));
      // CRITICAL: refresh the remembered slug. Without it every bare-/admin request keeps
      // sending X-Tenant-Slug = the OLD address (which no longer resolves) → 404 on the
      // calendar, availability and bookings until a full reload. Under /t/{slug}/admin
      // the store remembers nothing (the path names the practice; lib/api.js rule 1).
      rememberAdminTenantSlug(data.slug || data.subdomain);
    }
    if (customDomain && customDomain !== tenant?.custom_domain) {
      // A refused domain (taken by another practice, a platform address, a root domain
      // before root domains are available) does not block the rest of the publish, but
      // the practitioner is told — silently dropping it left them believing it was saved.
      try { await api.post("/tenant/custom-domain", { domain: customDomain }); }
      catch (e) {
        const code = apiErrorCode(e);
        toast.error((code && t(`cdom.err.${code}`, null, "")) || extractApiError(e, t("dom.error")));
      }
    }
    // Le backend attend {key, value_fr, value_en} (ContentBlockUpsert) et n'expose
    // que POST /admin/content (upsert) — l'ancien PUT /admin/content/{key} n'existe
    // pas (405) et les champs content_* étaient ignorés (contenu sauvé à vide).
    // The wizard has ONE input per slot: it writes the column of the language it is being
    // used in and never overwrites text the other language already has (buildContentUpserts
    // / lib/siteContent.js). An empty field is not a write, so a slot this language has
    // nothing for stays exactly as it is.
    const saved = { ...contentBlocks };
    for (const payload of buildContentUpserts(content, contentBlocks, lang)) {
      try {
        await api.post("/admin/content", payload);
        saved[payload.key] = payload;
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.debug("[SiteBuilder] content upsert:", payload.key, e?.message);
      }
    }
    setContentBlocks(saved);
    return effectiveSubdomain;
  };

  const refreshPreview = async () => {
    setBusy(true);
    try {
      await saveAll();
      setPreviewKey((k) => k + 1);
      toast.success(t("siteBuilder.toast.previewUpdated"));
    } catch (e) {
      saveFailed(e);
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    setBusy(true);
    // Ouvre l'onglet DANS le geste utilisateur (sinon bloqué par l'anti-popup),
    // la destination est fixée après la sauvegarde.
    const siteTab = window.open("", "_blank");
    try {
      const finalSubdomain = await saveAll();
      try { await api.post("/tenant/site/publish"); } catch { /* non-bloquant */ }
      toast.success(t("siteBuilder.toast.published"));
      // Real public address once published — falls back to the in-app preview path
      // only in the unlikely case no subdomain was ever set.
      const liveUrl = finalSubdomain ? publicSiteUrl(finalSubdomain) : `${window.location.origin}/t/${previewSlug}`;
      if (siteTab) siteTab.location = liveUrl;
    } catch (e) {
      if (siteTab) siteTab.close();
      saveFailed(e);
    } finally {
      setBusy(false);
    }
  };

  // Auto-sauvegarde + reload de l'aperçu géré dans goToStep (navigation).

  return (
    <div className="max-w-5xl mx-auto" data-testid="site-builder-wizard">
      <div className="text-center mb-10">
        <Sparkles className="w-10 h-10 mx-auto text-sol-gold mb-3" />
        <h1 className="font-display text-4xl text-eh-ink">{t("siteBuilder.title")}</h1>
        <p className="text-eh-muted mt-2">{t("siteBuilder.subtitle")}</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center justify-between mb-10">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const done = i < step, active = i === step;
          return (
            <React.Fragment key={s.id}>
              <button onClick={() => goToStep(i)} className={`flex flex-col items-center ${active || done ? "text-sol-gold" : "text-eh-muted"}`}>
                <div className={`w-11 h-11 rounded-full flex items-center justify-center border-2 ${active ? "bg-sol-gold border-sol-gold text-eh-ink" : done ? "border-sol-gold" : "border-eh-border-soft"}`} data-testid={`builder-step-${s.id}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <span className="text-xs mt-2 font-medium hidden sm:block">{t(s.labelKey)}</span>
              </button>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-2 ${done ? "bg-sol-gold" : "bg-eh-border-soft"}`} />}
            </React.Fragment>
          );
        })}
      </div>

      <div className="bg-white border border-eh-border-soft rounded-3xl p-8 min-h-[420px]">
        {/* STEP MODELE — galerie de modèles */}
        {step === 0 && (
          <>
            <h2 className="font-display text-2xl mb-2">{t("siteBuilder.template.title")}</h2>
            <p className="text-eh-muted text-sm mb-6">{t("siteBuilder.template.subtitle")}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {TEMPLATES.map((tpl) => {
                const selected = siteTemplate === tpl.id;
                return (
                  <button
                    key={tpl.id}
                    onClick={() => selectTemplate(tpl)}
                    data-testid={`template-card-${tpl.id}`}
                    className={`group text-left rounded-2xl border-2 overflow-hidden transition ${selected ? "border-sol-gold ring-2 ring-sol-gold/30" : "border-eh-border-soft hover:border-sol-gold/50"}`}
                  >
                    <div className="relative h-32 overflow-hidden">
                      <img src={tpl.preview_image} alt={tpl.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      {selected && <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-sol-gold flex items-center justify-center" data-testid={`template-selected-${tpl.id}`}><Check className="w-4 h-4 text-eh-ink" /></div>}
                      {tpl.id === "t1_classic" && <span className="absolute bottom-2 left-2 text-[10px] uppercase tracking-wider bg-white/90 text-eh-ink px-2 py-0.5 rounded-full">{t("siteBuilder.template.current")}</span>}
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="font-sol font-semibold text-sm">{tpl.name}</div>
                        <div className="flex gap-1">
                          {[tpl.colors.background, tpl.colors.primary, tpl.colors.accent].map((c, i) => <span key={i} className="w-3.5 h-3.5 rounded-full border border-black/5" style={{ background: c }} />)}
                        </div>
                      </div>
                      <div className="text-xs text-eh-muted mt-1">{tt(tpl.taglineKey)}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* STEP BRANDING */}
        {step === 1 && (
          <>
            <h2 className="font-display text-2xl mb-2">{t("siteBuilder.branding.title")}</h2>
            <p className="text-eh-muted text-sm mb-6">{t("siteBuilder.branding.subtitle")}</p>
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              {[["primary_color", "siteBuilder.branding.color.primary"], ["accent_color", "siteBuilder.branding.color.accent"], ["secondary_color", "siteBuilder.branding.color.secondary"]].map(([key, labelKey]) => (
                <div key={key}>
                  <Label className="text-xs">{t(labelKey)}</Label>
                  <div className="flex gap-2 mt-1.5">
                    <input type="color" value={branding[key]} onChange={(e) => setBranding({ ...branding, [key]: e.target.value })} className="w-12 h-10 rounded border cursor-pointer" data-testid={`builder-color-${key}`} />
                    <Input value={branding[key]} onChange={(e) => setBranding({ ...branding, [key]: e.target.value })} className="font-mono text-sm" />
                  </div>
                </div>
              ))}
            </div>
            <Label className="text-xs">{t("siteBuilder.branding.tagline.label")}</Label>
            <Input value={branding.tagline} onChange={(e) => setBranding({ ...branding, tagline: e.target.value })} data-testid="builder-tagline" placeholder={t("siteBuilder.branding.tagline.placeholder")} className="mt-1.5 mb-5" />
            <Label className="text-xs">{t("siteBuilder.branding.logo.label")}</Label>
            <div className="mt-1.5 flex items-center gap-4">
              {branding.logo_url ? (
                <img src={branding.logo_url} alt={t("siteBuilder.branding.logo.alt")} className="w-20 h-20 rounded-xl border border-eh-border-soft object-contain bg-white" />
              ) : (
                <div className="w-20 h-20 rounded-xl border-2 border-dashed border-eh-border-soft flex items-center justify-center text-eh-muted text-xs">{t("siteBuilder.branding.logo.none")}</div>
              )}
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" data-testid="builder-logo-upload"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const fd = new FormData();
                    fd.append("file", file);
                    try {
                      const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
                      const url = data.url || data.file_url || (data.path ? `/api/uploads/${data.path}` : "");
                      const full = url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
                      setBranding({ ...branding, logo_url: full });
                      toast.success(t("siteBuilder.toast.logoUploaded"));
                    } catch { toast.error(t("siteBuilder.toast.uploadError")); }
                  }}
                />
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-eh-border-soft hover:bg-eh-cream cursor-pointer text-sm">{t("siteBuilder.branding.logo.upload")}</div>
              </label>
              {branding.logo_url && <button onClick={() => setBranding({ ...branding, logo_url: "" })} className="text-xs text-rose-500 hover:underline">{t("siteBuilder.branding.logo.remove")}</button>}
            </div>
          </>
        )}

        {/* STEP CONTENU */}
        {step === 2 && (
          <>
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-display text-2xl">{t("siteBuilder.content.title")}</h2>
              <div className="flex items-center gap-2">
                <span className="text-xs text-eh-muted font-medium uppercase tracking-wider">Editing:</span>
                <span className="text-xs px-2.5 py-1 rounded-full bg-eh-ink text-eh-bg font-mono font-semibold uppercase">{lang}</span>
                <div className="flex items-center gap-1 ml-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono ${contentBlocks["hero.title"]?.value_fr ? "bg-emerald-100 text-emerald-800 font-semibold" : "bg-eh-border-soft text-eh-muted"}`}>FR {contentBlocks["hero.title"]?.value_fr && "✓"}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono ${contentBlocks["hero.title"]?.value_en ? "bg-emerald-100 text-emerald-800 font-semibold" : "bg-eh-border-soft text-eh-muted"}`}>EN {contentBlocks["hero.title"]?.value_en && "✓"}</span>
                </div>
              </div>
            </div>
            <p className="text-eh-muted text-sm mb-6">{t("siteBuilder.content.subtitle")}</p>
            <div className="space-y-4">
              <div>
                <Label className="text-xs">{t("siteBuilder.content.heroTitle.label")}</Label>
                {/* Multi-line: the title is one value, a line per line break, shared with
                    Site content. A single-line input would strip the breaks, and the Publish
                    auto-save would write the flattened title back. */}
                <textarea value={content.heroTitle} onChange={(e) => setContent({ ...content, heroTitle: e.target.value })} data-testid="builder-hero-title" rows={3} className="mt-1.5 w-full border rounded-md p-3 text-sm" />
              </div>
              <div>
                <Label className="text-xs">{t("siteBuilder.content.heroSubtitle.label")}</Label>
                <Input value={content.heroSub} onChange={(e) => setContent({ ...content, heroSub: e.target.value })} data-testid="builder-hero-subtitle" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs">{t("siteBuilder.content.servicesText.label")}</Label>
                <textarea value={content.servicesLead} onChange={(e) => setContent({ ...content, servicesLead: e.target.value })} data-testid="builder-services" rows={5} className="mt-1.5 w-full border rounded-md p-3 text-sm" />
              </div>
            </div>
          </>
        )}

        {/* STEP SECTIONS */}
        {step === 3 && (
          <>
            <h2 className="font-display text-2xl mb-2">{t("siteBuilder.sections.stepTitle")}</h2>
            <p className="text-eh-muted text-sm mb-6">{t("siteBuilder.sections.stepSubtitle")}</p>
            <div className="space-y-3">
              {SECTIONS.map((s) => (
                <div key={s.key} className="flex items-center justify-between p-4 rounded-2xl border border-eh-border-soft">
                  <span className="font-medium text-eh-ink">{t(s.labelKey)}</span>
                  <button
                    onClick={() => toggleSection(s.key)}
                    data-testid={`section-toggle-${s.key}`}
                    role="switch"
                    aria-checked={isOn(s.key)}
                    className={`relative w-12 h-7 rounded-full transition-colors ${isOn(s.key) ? "bg-sol-gold" : "bg-eh-border-soft"}`}
                  >
                    <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform ${isOn(s.key) ? "translate-x-5" : ""}`} />
                  </button>
                </div>
              ))}
            </div>
            <p className="text-xs text-eh-muted mt-4">{t("siteBuilder.sections.footerNote")}</p>
          </>
        )}

        {/* STEP ADRESSE — slug + domaine */}
        {step === 4 && (
          <>
            <h2 className="font-display text-2xl mb-2">{t("siteBuilder.address.title")}</h2>
            <p className="text-eh-muted text-sm mb-6">{t("siteBuilder.address.subtitle")}</p>
            <Label className="text-xs">{t("siteBuilder.address.subdomain.label")}</Label>
            <div className="flex items-center bg-eh-cream/40 border border-eh-border-soft rounded-xl overflow-hidden mt-1.5 mb-5">
              <input
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 60))}
                data-testid="builder-slug"
                placeholder={t("siteBuilder.address.subdomain.placeholder")}
                className="flex-1 bg-transparent border-0 py-2.5 px-3 text-sm focus:outline-none"
              />
              <span className="px-3 text-eh-muted text-sm whitespace-nowrap border-l border-eh-border-soft">.{PLATFORM_DOMAIN}</span>
            </div>
            {/* Renommage : le praticien doit savoir ce que ça implique AVANT de publier.
                L'ancienne adresse reste réservée et redirige (cf. set_subdomain), donc
                aucun lien ne casse et personne ne peut la revendiquer. */}
            {tenant?.subdomain && subdomain && subdomain !== tenant.subdomain && (
              <div
                data-testid="builder-rename-notice"
                className="-mt-3 mb-5 rounded-xl border border-eh-ochre-deep/40 bg-eh-ochre/10 px-4 py-3 text-xs text-eh-ink"
              >
                <strong>{t("siteBuilder.address.renameNotice.title")}</strong> {t("siteBuilder.address.renameNotice.from")}{" "}
                <span className="font-mono">{tenant.subdomain}.{PLATFORM_DOMAIN}</span> {t("siteBuilder.address.renameNotice.to")}{" "}
                <span className="font-mono">{subdomain}.{PLATFORM_DOMAIN}</span> {t("siteBuilder.address.renameNotice.onPublish")}
                {" "}{t("siteBuilder.address.renameNotice.body")}
              </div>
            )}
            <Label className="text-xs">{t("siteBuilder.address.customDomain.label")}</Label>
            <Input
              value={customDomain}
              onChange={(e) => { setCustomDomain(e.target.value); clearFieldError("customDomain"); }}
              data-testid="builder-custom-domain"
              placeholder={t("siteBuilder.address.customDomain.placeholder")}
              className={`mt-1.5 ${fieldErrors.customDomain ? invalidInputClass : ""}`}
            />
            <FieldError message={fieldErrors.customDomain ? t(fieldErrors.customDomain) : ""} testId="builder-custom-domain-error" />
            <div className="mt-6 bg-eh-cream/40 rounded-2xl p-4 text-xs text-eh-muted">
              <div className="font-medium text-eh-ink mb-1">{t("siteBuilder.address.cname.title")}</div>
              {t("siteBuilder.address.cname.before")} <code className="bg-white px-1.5 py-0.5 rounded">{PLATFORM_DOMAIN}</code>. {t("siteBuilder.address.cname.after")}
            </div>
          </>
        )}

        {/* STEP PUBLIER — aperçu live */}
        {step === 5 && (
          <div className="text-center">
            <Rocket className="w-11 h-11 mx-auto text-sol-gold mb-2" />
            <h2 className="font-display text-3xl mb-1">{t("siteBuilder.publish.title")}</h2>
            <p className="text-eh-muted mb-4 max-w-md mx-auto text-sm">{t("siteBuilder.publish.subtitle")}</p>
            <div className="flex items-center justify-center gap-3 mb-4 flex-wrap">
              <div className="bg-eh-cream/40 rounded-xl px-4 py-2 text-left">
                <div className="text-[10px] uppercase tracking-wider text-eh-muted">{t("siteBuilder.publish.urlLabel")}</div>
                <div className="font-mono text-eh-ink text-sm">{liveSiteUrl || `https://....${PLATFORM_DOMAIN}`}</div>
              </div>
              <Button variant="outline" size="sm" onClick={refreshPreview} disabled={busy} data-testid="builder-refresh-preview">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />} {t("siteBuilder.publish.refreshPreview")}
              </Button>
            </div>
            {previewSlug && (
              <div className="mb-6 rounded-2xl border border-eh-border-soft overflow-hidden bg-eh-cream/30">
                {/* Same-origin, authenticated in-app preview — the backend resolves /api/t/{slug}/
                    by the tenant's `slug` field, not `subdomain`, so this stays path-based (no DNS needed). */}
                <iframe key={previewKey} src={`/t/${previewSlug}?_=${previewKey}`} title={t("siteBuilder.publish.previewIframeTitle")} data-testid="builder-iframe-preview" className="w-full h-[520px]" />
              </div>
            )}
            <div className="flex gap-3 justify-center flex-wrap">
              <Button onClick={publish} disabled={busy} data-testid="builder-publish" className="bg-sol-gold hover:bg-sol-gold/90 text-eh-ink text-base px-8 py-6">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Rocket className="w-4 h-4 mr-2" />} {t("siteBuilder.publish.publishButton")}
              </Button>
              <Button variant="outline" onClick={() => window.open(liveSiteUrl || `/t/${previewSlug}`, "_blank")} className="text-base px-6 py-6">
                <ExternalLink className="w-4 h-4 mr-2" /> {t("siteBuilder.publish.openInTab")}
              </Button>
            </div>
          </div>
        )}
      </div>

      {step < STEPS.length - 1 && (
        <div className="flex justify-between mt-6">
          <Button variant="outline" onClick={prev} disabled={step === 0} data-testid="builder-prev">
            <ChevronLeft className="w-4 h-4 mr-1" /> {tc("actions.previous")}
          </Button>
          <Button onClick={next} data-testid="builder-next" className="bg-eh-ink text-eh-bg">
            {tc("actions.continue")} <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}

      <div className="text-center mt-6">
        <button onClick={() => navigate(adminHref("/admin"))} className="text-xs text-eh-muted hover:underline">{t("siteBuilder.backToDashboard")}</button>
      </div>
    </div>
  );
}
